import React, { useState } from "react";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import { SkeletonText } from "../UI/DesignStandards/Skeleton";
import { formatCurrency, formatPosition } from "../UI";

const BowlerPrizeSummary = ({ bowler, tournamentId, loading = false }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  if (loading) {
    return (
      <div className="card-board overflow-hidden">
        <div className="p-4 border-b border-brand-charcoal/10">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <SkeletonText size="lg" width="48" className="mb-2" />
              <div className="flex items-center gap-4 mt-2">
                <div className="flex flex-col items-center gap-1">
                  <span className="text-xs text-brand-muted uppercase tracking-wide">
                    Total
                  </span>
                  <SkeletonText size="md" width="24" />
                </div>
                <div className="flex flex-col items-center gap-1">
                  <span className="text-xs text-brand-muted uppercase tracking-wide">
                    Individual
                  </span>
                  <SkeletonText size="md" width="20" />
                </div>
                <div className="flex flex-col items-center gap-1">
                  <span className="text-xs text-brand-muted uppercase tracking-wide">
                    Team
                  </span>
                  <SkeletonText size="md" width="16" />
                </div>
              </div>
            </div>
            <SkeletonText size="md" width="8" height="8" className="rounded" />
          </div>
        </div>
        <div className="px-4 py-3 bg-brand-cream/60 border-t border-brand-charcoal/10">
          <div className="flex items-center justify-between">
            <SkeletonText size="sm" width="20" />
            <SkeletonText size="sm" width="24" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card-board overflow-hidden">
      <div className="p-4 border-b border-brand-charcoal/10">
        <div className="flex items-center justify-between">
          <div className="flex-1 min-w-0">
            <h3 className="heading-display text-lg truncate">
              {bowler.bowlerName || "Unknown Bowler"}
            </h3>
            <div className="flex items-center gap-4 mt-2 text-sm text-brand-charcoal">
              <div className="flex flex-col items-center gap-1">
                <span className="text-xs text-brand-muted uppercase tracking-wide">
                  Total
                </span>
                <span className="font-score font-medium">
                  {formatCurrency(bowler.totalPrizes)}
                </span>
              </div>
              <div className="flex flex-col items-center gap-1">
                <span className="text-xs text-brand-muted uppercase tracking-wide">
                  Individual
                </span>
                <span className="font-score">
                  {formatCurrency(bowler.individualPrizes)}
                </span>
              </div>
              <div className="flex flex-col items-center gap-1">
                <span className="text-xs text-brand-muted uppercase tracking-wide">
                  Team
                </span>
                <span className="font-score">
                  {formatCurrency(bowler.teamPrizes)}
                </span>
              </div>
            </div>
          </div>

          <button
            onClick={toggleExpanded}
            className="ml-4 p-2 text-brand-mist hover:text-brand-charcoal transition-colors"
            aria-label={isExpanded ? "Collapse details" : "Expand details"}
          >
            {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
          </button>
        </div>
      </div>

      {isExpanded && (
        <div className="border-t border-brand-charcoal/10">
          <div className="p-4">
            <h4 className="text-sm font-medium text-brand-charcoal mb-3">
              Event Standings
            </h4>
            <div className="space-y-3">
              {bowler.events.map((event, index) => (
                <div
                  key={`${bowler.bowlerId}-${event.teamId || "no-team"}-${
                    event.eventId || index
                  }`}
                  className="flex items-center justify-between p-3 card-inset"
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-brand-charcoal">
                      {event.eventName || "Unknown Event"}
                    </div>
                    <div className="text-sm text-brand-muted">
                      {event.isIndividualEvent
                        ? "Individual Event"
                        : "Team Event"}
                      {event.teamName && !event.isIndividualEvent && (
                        <span className="ml-2">• {event.teamName}</span>
                      )}
                    </div>
                  </div>
                  <div className="text-right ml-3">
                    <div className="font-score font-semibold text-brand-charcoal">
                      {formatPosition(event.position)}
                    </div>
                    <div className="text-sm font-score font-medium text-brand-charcoal">
                      {formatCurrency(event.prize)}
                    </div>
                    {event.tieDescription && (
                      <div className="text-xs text-brand-muted mt-1">
                        {event.tieDescription}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="px-4 py-3 bg-brand-cream/60 border-t border-brand-charcoal/10">
        <div className="flex items-center justify-between text-sm text-brand-muted">
          <span>
            {bowler.events.length} event{bowler.events.length !== 1 ? "s" : ""}
          </span>
          <button
            onClick={toggleExpanded}
            className="text-brand-gold-ink hover:underline font-medium"
          >
            {isExpanded ? "Show Less" : "View Details"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BowlerPrizeSummary;
